import React, { useEffect, useState } from 'react';
import { ExampleLinkWorkflow, WorkflowState } from '../../../api/cafAutomation';
import { CafClusterSample, CafCorpusEntry, getCafCluster, getEmailPreview } from '../../../api/cafCorpus';
import { STATE_LABEL } from '../automation/automationCopy';
import { StepChips } from '../automation/StepChips';
import { AllEmails, EmailSamples } from '../EmailSamples';
import { formatCount } from '../format';
import { Icon } from '../Icon';
import { ActionPill } from '../LabelForm';
import { senderEmail } from '../lenders';
import { Drawer } from '../Overlay';
import { whoAndWhen } from './useCurrentEntry';

interface Props {
  entry: CafCorpusEntry;
  lenderOf: (sender: string) => string | null;
  onClose: () => void;
  onEdit: (entry: CafCorpusEntry) => void;
  onHistory: (entry: CafCorpusEntry) => void;
  /** Automation: null means None; omitted on an older backend. */
  workflow?: ExampleLinkWorkflow | null;
  onOpenWorkflow?: (workflowId: string) => void;
}

export const ExampleSummary: React.FC<{ entry: CafCorpusEntry; covers?: number | null }> = ({ entry, covers }) => {
  const fields = entry.extraction_schema?.enabled ? entry.extraction_schema.fields : [];
  return (
    <div className="cur-summary-box">
      <dl className="cur-kv">
        <dt>What happens</dt>
        <dd><ActionPill action={entry.correct_action} />{entry.active === false && <> <span className="cur-pill cur-pill--grey">Off</span></>}</dd>
        <dt>Why</dt><dd>{entry.note || '—'}</dd>
        <dt>Source</dt>
        <dd>{entry.cluster_id == null ? 'From one email' : `From a group of ${covers != null ? formatCount(covers) : 'several'} emails`}</dd>
        {entry.category_override && <><dt>Classification</dt><dd>{entry.category_override}</dd></>}
        {fields.length > 0 && <><dt>Copies into claim</dt><dd>{fields.map((field) => field.label).join(', ')}</dd></>}
        {entry.archive_override_refs && <><dt>References</dt><dd>Also archives emails that quote a reference</dd></>}
        <dt>Last changed</dt><dd>{whoAndWhen(entry) || '—'}</dd>
      </dl>
    </div>
  );
};

const WORKFLOW_SENTENCE: Record<WorkflowState, string> = {
  on: 'When the overall AI is On, this workflow can run for future emails.',
  watching: 'The workflow is Watching: future decisions are recorded as proposals without acting on emails.',
  paused: 'The workflow is paused; it does not act on emails.',
  draft: 'The workflow is a draft; it does not act on emails.',
};

const WorkflowLine: React.FC<{ workflow: ExampleLinkWorkflow | null; onOpen?: (workflowId: string) => void }> = ({ workflow, onOpen }) => (
  <div className="cur-summary-box" style={{ marginTop: 10 }}>
    {workflow ? (
      <>
        <div className="cur-inline">
          <span className="cur-muted">Workflow:</span>
          <strong>{`${workflow.name} (${STATE_LABEL[workflow.state]})`}</strong>
          {onOpen && <button type="button" className="cur-linkish" onClick={() => onOpen(workflow.workflow_id)}>Open in Automation</button>}
        </div>
        <div style={{ marginTop: 6 }}><StepChips steps={workflow.steps} /></div>
        <p className="cur-hint" style={{ marginTop: 6 }}>{WORKFLOW_SENTENCE[workflow.state]}</p>
      </>
    ) : (
      <div className="cur-inline"><span className="cur-muted">Workflow:</span><span>None</span></div>
    )}
  </div>
);

export const ExampleDrawer: React.FC<Props> = ({ entry, lenderOf, onClose, onEdit, onHistory, workflow, onOpenWorkflow }) => {
  const [samples, setSamples] = useState<CafClusterSample[] | null>(null);
  const [total, setTotal] = useState<number | null>(entry.group_size ?? null);
  const [problem, setProblem] = useState('');

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        if (entry.cluster_id) {
          const group = await getCafCluster(entry.cluster_id);
          if (!alive) return;
          setSamples(group.samples);
          setTotal(group.member_count);
        } else if (entry.email_task_id) {
          const one = await getEmailPreview(entry.email_task_id);
          if (!alive) return;
          setSamples([one]);
          setTotal(1);
        } else {
          setProblem('These emails are no longer available.');
        }
      } catch (err) {
        if (!alive) return;
        const status = (err as { response?: { status?: number } })?.response?.status;
        setProblem(status === 404 ? 'These emails are no longer available.' : "Couldn't load the emails. Close and try again.");
      }
    };
    void load();
    return () => { alive = false; };
  }, [entry.cluster_id, entry.email_task_id]);

  const covers = entry.group_size ?? total;
  return (
    <Drawer eyebrow="Approved example" title={lenderOf(entry.sender) ?? senderEmail(entry.sender)}
      subtitle={`${senderEmail(entry.sender)}${covers != null ? ` · covers ${formatCount(covers)} ${covers === 1 ? 'email' : 'emails'}` : ''}`}
      onClose={onClose}
      footer={<button type="button" className="ui-btn ui-btn--ghost ui-btn--md" onClick={onClose}>Close</button>}>
      <section>
        <ExampleSummary entry={entry} covers={covers} />
        {workflow !== undefined && <WorkflowLine workflow={workflow} onOpen={onOpenWorkflow} />}
        <div className="cur-btn-row" style={{ marginTop: 10 }}>
          {entry.active !== false && (
            <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm" onClick={() => onEdit(entry)}>
              <Icon name="pencil" size="sm" />Edit
            </button>
          )}
          <button type="button" className="ui-btn ui-btn--ghost ui-btn--sm" onClick={() => onHistory(entry)}>
            <Icon name="clock" size="sm" />History
          </button>
        </div>
      </section>
      <section>
        <h3 className="cur-sec-title">
          Emails in this group
          {samples && total != null && <span className="cur-muted">(showing {samples.length} of {formatCount(total)})</span>}
        </h3>
        {problem && <p className="cur-hint">{problem}</p>}
        {!problem && !samples && <p className="cur-hint">Loading…</p>}
        {samples && <EmailSamples samples={samples} allowMarkFinal={false} />}
        {samples && entry.cluster_id && total != null && total > samples.length && (
          <div style={{ marginTop: 10 }}><AllEmails clusterId={entry.cluster_id} total={total} allowMarkFinal={false} /></div>
        )}
      </section>
    </Drawer>
  );
};
